import {pickDefined} from '@benchling/js-utils';

import {Permissions} from 'auth/PermissionConstants';
import {
  AnalyticsQuerySpecRequest,
  AnalyticsQuerySpecResponse,
} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import {RESTAnalyticsDashboardLayoutRow} from 'insights/layouts/RESTLayoutTypes';
import {CONFIG_TYPENAME_TO_PARAMETER_TYPE} from 'insights/parameters/ParameterConstants';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import {
  AnalyticsParameterConfigSpecRequest,
  RESTAnalyticsBioentityLinkParameterConfig,
  RESTAnalyticsParameterValue,
  RESTAnalyticsProcessLinkParameterConfig,
  RESTAnalyticsProcessTaskLinkParameterConfig,
  RESTAnalyticsSelectorParameterConfig,
  RESTAnalyticsStorableLinkParameterConfig,
  RESTAnalyticsStudyLinkParameterConfig,
} from 'insights/parameters/RESTParameterTypes';
import {MakeFieldsOptional} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

export interface AnalyticsDashboardSpecResponse {
  id: string;
  analyticsQueries: Array<AnalyticsQuerySpecResponse>;
  description: string;
  name: string;
  layoutRows: Array<RESTAnalyticsDashboardLayoutRow>;
  modifiedAt: string;
  parameterConfigsGQL: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<RESTAnalyticsParameterValue>;
  permissions: Permissions;
  projectId: string;
  url: string;
}
export type AnalyticsDashboardSpecRequest = MakeFieldsOptional<
  Omit<
    AnalyticsDashboardSpecResponse,
    'analyticsQueries' | 'modifiedAt' | 'parameterConfigsGQL' | 'permissions' | 'url'
  > & {
    analyticsQueries: Array<AnalyticsQuerySpecRequest>;
    parameterConfigs: Array<AnalyticsParameterConfigSpecRequest>;
  },
  'id'
>;

export type UpdateAnalyticsDashboardSpecRequest = AnalyticsDashboardSpecRequest & {
  clientModifiedAt: string;
};

export function getRestTypeForGraphQLParameterConfig(config: AnalyticsParameterConfigGraphQL): ParameterType {
  return CONFIG_TYPENAME_TO_PARAMETER_TYPE[config.__typename];
}

export function getParameterConfigSpecFromReadShape(
  config: AnalyticsParameterConfigGraphQL
): AnalyticsParameterConfigSpecRequest {
  const baseSpec: AnalyticsParameterConfigSpecRequest = {
    ...pickDefined(config, 'id', 'isMulti', 'systemName'),
    type: getRestTypeForGraphQLParameterConfig(config),
  };
  if (config.__typename === 'AnalyticsParameterBioentityLinkConfigNode') {
    return {
      ...baseSpec,
      tagSchemaId: config.tagSchema?.id,
    } as RESTAnalyticsBioentityLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterProcessTaskLinkConfigNode') {
    return {
      ...baseSpec,
      processTaskSchemaId: config.processTaskSchema?.id,
    } as RESTAnalyticsProcessTaskLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterProcessLinkConfigNode') {
    return {
      ...baseSpec,
      processTaskSchemaId: config.processTaskSchema?.id,
    } as RESTAnalyticsProcessLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterSelectorConfigNode') {
    return {
      ...baseSpec,
      schemaFieldSelectorId: config.schemaFieldSelector?.id,
    } as RESTAnalyticsSelectorParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterStorableLinkConfigNode') {
    return {
      ...baseSpec,
      storableSchemaId: config.storableSchema?.id,
    } as RESTAnalyticsStorableLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterStudyLinkConfigNode') {
    return {
      ...baseSpec,
      studySchemaId: config.studySchema?.id,
    } as RESTAnalyticsStudyLinkParameterConfig;
  }

  return baseSpec;
}

export function getParameterConfigsSpecFromReadShape(
  configs: Array<AnalyticsParameterConfigGraphQL>
): Array<AnalyticsParameterConfigSpecRequest> {
  return configs.map((config) => getParameterConfigSpecFromReadShape(config));
}
